import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Stat, Pill, StackedAssuranceBar, LadderExplainer, Button, EmptyState, NarrativeCard } from "@/design/components";
import {
  useSeed,
  useMoney,
  useOrgIndex,
  assuranceOf,
  spendHasGap,
} from "@/domain/hooks";
import { useVerifySpend } from "@/domain/useMutations";
import type { AssuranceLevel, SpendRequest } from "@/domain/types";
import { SpendAskDrawer } from "@/screens/approval/SpendAskDrawer";

export function AssuranceView() {
  const seed = useSeed();
  const money = useMoney();
  const { verify, isPending: verifying } = useVerifySpend();
  const orgs = useOrgIndex();
  const [open, setOpen] = useState<SpendRequest | null>(null);

  // Only spend where money is considered out counts toward assurance coverage.
  const spends = seed.spendRequests.filter(
    (s) => s.state === "committed" || s.state === "evidence_submitted" || s.state === "confirmed",
  );
  const leveled = spends.map((s) => ({ s, level: assuranceOf(s, seed.evidence ?? [], seed.spendItems) }));

  const byLevel: Record<AssuranceLevel, number> = { self_reported: 0, document_backed: 0, statement_matched: 0, verified: 0 };
  for (const { s, level } of leveled) byLevel[level] += s.amount;
  const total = Object.values(byLevel).reduce((a, b) => a + b, 0) || 1;
  const assured = byLevel.statement_matched + byLevel.verified;
  const gaps = leveled
    .filter(({ s, level }) => spendHasGap(s, level))
    .sort((a, b) => (a.s.gapExplanation ? 1 : -1) - (b.s.gapExplanation ? 1 : -1));
  const unexplained = gaps.filter(({ s }) => !s.gapExplanation);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Observed spend" value={money(total)} />
        <Stat label="Assured" value={`${Math.round((assured / total) * 100)}%`} sub="matched or verified" subTone={assured / total >= 0.6 ? "ok" : "warn"} />
        <Stat label="Self-reported gaps" value={String(gaps.length)} sub={money(byLevel.self_reported)} subTone={gaps.length ? "warn" : "ok"} />
        <Stat label="Unexplained" value={String(unexplained.length)} sub="need evidence or a note" subTone={unexplained.length ? "bad" : "ok"} hero={unexplained.length > 0} />
      </div>

      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-faint">Assurance coverage by value</h2>
          <LadderExplainer />
        </div>
        <StackedAssuranceBar counts={byLevel} formatAmount={money} />
      </section>

      <NarrativeCard
        variant="briefing"
        title="How oversight assures spend"
        body="Spend climbs the assurance ladder as it is documented, reconciled against a bank or mobile-money statement, and then independently verified by Stratus through risk-based sampling and site visits. The gaps below are spend that is out but only self-reported. Verify it, or have the contractor explain why no document or match exists."
      />

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-faint">Gaps needing attention</h2>
          <span className="text-xs text-faint">{gaps.length} item{gaps.length === 1 ? "" : "s"}</span>
        </div>
        {gaps.length === 0 ? (
          <EmptyState title="No open assurance gaps" body="Every figure with money out is documented, matched or verified." />
        ) : (
          <div className="overflow-hidden rounded-lg border border-line bg-card">
            {gaps.map(({ s }) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{s.title}</p>
                  <p className="text-xs text-faint">
                    {orgs[s.organisationId]?.name} · {seed.projects.find((p) => p.id === s.projectId)?.name}
                  </p>
                  {s.gapExplanation && (
                    <p className="mt-1 line-clamp-1 text-xs text-mute">{s.gapExplanation.body}</p>
                  )}
                </div>
                {s.gapExplanation ? <Pill variant="warn">Explained</Pill> : <Pill variant="danger">Unexplained</Pill>}
                <span className="text-sm font-medium tabular-nums text-ink">{money(s.amount)}</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" onClick={() => setOpen(s)}>Open</Button>
                  <Button size="sm" variant="secondary" disabled={verifying} onClick={async () => { await verify(s.id); toast.success(`Verified '${s.title}'`); }}>
                    <ShieldCheck className="h-4 w-4" /> Verify
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <SpendAskDrawer ask={open} open={!!open} onOpenChange={(o) => !o && setOpen(null)} canApprove={false} />
    </div>
  );
}
